import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { ListTodo } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PlainMessage } from "@bufbuild/protobuf";
import FlexBox from "components/common/FlexBox";
import ListDocumentView from "components/documents/ListDocumentView";
import { useGetDocument, useUpdateDocument } from "services/documents";
import {
  GetDocumentRequest,
  UpdateDocumentRequest,
} from "protogen/documents_service_pb";
import { Document } from "protogen/documents_pb";
import { Attachment, UploadAttachment } from "protogen/common_pb";
import { ContentUpdates } from "protogen/notes_pb";
import { GetMemberDetailsResponse } from "protogen/member_service_pb";
import useIsMobile from "components/hooks/useIsMobile";

const TODO_LIST_TITLE = "Todo List";

const getDocumentHash = (document: Document | undefined): string => {
  if (!document) return "";
  return document.note?.hash || "";
};

export default function TodoList({
  data,
}: {
  data: GetMemberDetailsResponse | null;
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [document, setDocument] = useState<Document | undefined>();
  const [content, setContent] = useState<string | undefined>();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const currentHashRef = useRef("");

  const todoDoc = data?.coreDocuments?.find((d) => d.title === TODO_LIST_TITLE);

  const { request: getDocument, loading } = useGetDocument((r) => {
    setDocument(r.document);
    setContent(r.document?.note?.content?.payload || "");
    setAttachments(r.document?.note?.content?.attachments || []);
    currentHashRef.current = getDocumentHash(r.document);
  });

  const { request: updateDocument } = useUpdateDocument((r) => {
    setDocument(r.document);
    currentHashRef.current = getDocumentHash(r.document);
  });

  useEffect(() => {
    if (todoDoc?.documentRef) {
      getDocument(
        new GetDocumentRequest({
          documentRef: todoDoc.documentRef,
        }),
      );
    }
  }, [todoDoc?.documentRef]);

  const handleContentChange = useCallback(
    async (
      payload: string,
      textContent: string,
      uploadAttachments: PlainMessage<UploadAttachment>[],
    ) => {
      if (!document?.ref) return;
      await updateDocument(
        new UpdateDocumentRequest({
          documentRef: document.ref,
          content: new ContentUpdates({
            contentType: "json",
            payload,
            textContent,
            attachments: uploadAttachments,
          }),
          shouldUpdateContent: true,
          prevHash: currentHashRef.current,
        }),
      );
    },
    [document?.ref, updateDocument],
  );

  if (!todoDoc) return null;

  const documentLink = `/documents/${encodeURIComponent(todoDoc.documentRef)}`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: !isMobile ? "16px 20px" : undefined,
      }}
    >
      <FlexBox direction="row" justifyContent="space-between" flex={0}>
        <FlexBox direction="row" gap="8px" alignItems="center" flex={0}>
          <ListTodo size={20} color="#198282" style={{ flexShrink: 0 }} />
          <Typography variant={isMobile ? "h3" : "h4"} noWrap>
            {TODO_LIST_TITLE}
          </Typography>
        </FlexBox>
        <Button
          variant="text"
          sx={{ height: "auto", minWidth: "unset" }}
          onClick={() => navigate(documentLink)}
        >
          Open
        </Button>
      </FlexBox>
      {loading && (
        <Typography variant="body" color="text.secondary">
          Loading...
        </Typography>
      )}
      {!loading && document && content !== undefined && (
        <ListDocumentView
          document={document}
          title={document.title || TODO_LIST_TITLE}
          content={content}
          editable={true}
          initialAttachments={attachments}
          onContentChange={handleContentChange}
          embedded={true}
        />
      )}
    </Box>
  );
}
